/**
 * Document Merger Service
 * Extracts, translates, and consolidates multiple DOCX transcripts
 */

import * as docx from 'docx';
import { saveAs } from 'file-saver';
import { APP_VERSION } from '../config/constants';

// Brand colours (same as documentService)
const COLORS = {
  primary: '4f46e5',
  primaryDark: '3730a3',
  text: '1e293b',
  textLight: '64748b',
  textMuted: '94a3b8',
  background: 'f8fafc',
  backgroundAlt: 'f1f5f9',
  border: 'e2e8f0',
  white: 'ffffff',
};

export interface SourceDocument {
  id: string;
  filename: string;
  content: string;
  wordCount: number;
  extractedAt: Date;
}

export interface MergeOptions {
  projectTitle: string;
  translateToBritishEnglish: boolean;
  includeSourceReferences: boolean;
  generateSummary: boolean;
}

/**
 * Extract text content from a DOCX file
 */
export async function extractDocxContent(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  
  // Use mammoth.js for extraction (loaded dynamically)
  const mammoth = await import('mammoth');
  const result = await mammoth.extractRawText({ arrayBuffer });
  
  return result.value;
}

/**
 * Count words in text
 */
export function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(w => w.length > 0).length;
}

/**
 * Generate consolidated DOCX from merged content
 */
export async function generateConsolidatedDocx(
  sources: SourceDocument[],
  mergedContent: string,
  summary: string | null,
  options: MergeOptions
): Promise<void> {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const totalWords = sources.reduce((sum, s) => sum + s.wordCount, 0);

  const doc = new docx.Document({
    creator: `LinguistPro ${APP_VERSION}`,
    title: options.projectTitle || 'Consolidated Transcript Record',
    description: 'Merged transcription documents with British English translation',
    styles: {
      default: {
        document: {
          run: {
            font: 'Calibri',
            size: 22,
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1134, bottom: 1134, left: 1134, right: 1134 },
          },
        },
        headers: {
          default: createHeader(),
        },
        footers: {
          default: createFooter(),
        },
        children: [
          // Cover page
          ...createCoverPage(options.projectTitle, dateStr, timeStr, sources.length, totalWords),
          
          // Source documents table
          createPageBreak(),
          ...createSourcesSection(sources),
          
          // Executive summary (if generated)
          ...(summary ? [createPageBreak(), ...createSummarySection(summary)] : []),
          
          // Main consolidated content
          createPageBreak(),
          ...createContentSection(mergedContent, options.includeSourceReferences),
          
          // End section
          ...createEndSection(),
        ],
      },
    ],
  });

  const blob = await docx.Packer.toBlob(doc);
  const filename = `${options.projectTitle.replace(/[^a-z0-9]/gi, '_')}_CONSOLIDATED.docx`;
  saveAs(blob, filename);
}

// --- Helper Functions ---

function createHeader(): docx.Header {
  return new docx.Header({
    children: [
      new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: 'LINGUISTPRO',
            bold: true,
            size: 16,
            color: COLORS.primary,
          }),
          new docx.TextRun({
            text: '  |  CONSOLIDATED TRANSCRIPT',
            size: 14,
            color: COLORS.textMuted,
          }),
        ],
        border: {
          bottom: { color: COLORS.border, space: 1, style: docx.BorderStyle.SINGLE, size: 6 },
        },
        spacing: { after: 200 },
      }),
    ],
  });
}

function createFooter(): docx.Footer {
  return new docx.Footer({
    children: [
      new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: 'CONFIDENTIAL - CONSOLIDATED RECORD',
            size: 14,
            color: COLORS.textMuted,
          }),
          new docx.TextRun({ text: '\t\t' }),
          new docx.TextRun({
            children: ['Page ', docx.PageNumber.CURRENT, ' of ', docx.PageNumber.TOTAL_PAGES],
            size: 14,
            color: COLORS.textMuted,
          }),
        ],
        border: {
          top: { color: COLORS.border, space: 1, style: docx.BorderStyle.SINGLE, size: 6 },
        },
        spacing: { before: 200 },
        tabStops: [{ type: docx.TabStopType.RIGHT, position: docx.convertInchesToTwip(6) }],
      }),
    ],
  });
}

function createCoverPage(
  title: string,
  date: string,
  time: string,
  docCount: number,
  wordCount: number
): (docx.Paragraph | docx.Table)[] {
  return [
    new docx.Paragraph({
      children: [new docx.TextRun({ text: '◆', size: 48, color: COLORS.primary })],
      alignment: docx.AlignmentType.CENTER,
      spacing: { before: 600, after: 200 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: 'CONSOLIDATED',
          bold: true,
          size: 56,
          color: COLORS.text,
          font: 'Calibri Light',
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 0 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: 'TRANSCRIPT RECORD',
          bold: true,
          size: 56,
          color: COLORS.primary,
          font: 'Calibri Light',
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 400 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: title || 'Merged Transcription Documents',
          size: 28,
          color: COLORS.textLight,
          italics: true,
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 600 },
    }),
    // Metadata table
    createMetadataTable(date, time, docCount, wordCount),
    new docx.Paragraph({ spacing: { after: 400 } }),
  ];
}

function createMetadataTable(date: string, time: string, docCount: number, wordCount: number): docx.Table {
  const createRow = (label: string, value: string, highlight = false): docx.TableRow => {
    return new docx.TableRow({
      children: [
        new docx.TableCell({
          children: [
            new docx.Paragraph({
              children: [
                new docx.TextRun({ text: label, bold: true, size: 18, color: COLORS.textMuted }),
              ],
              spacing: { before: 80, after: 80 },
              indent: { left: 200 },
            }),
          ],
          width: { size: 40, type: docx.WidthType.PERCENTAGE },
          shading: { fill: COLORS.background },
          verticalAlign: docx.VerticalAlign.CENTER,
        }),
        new docx.TableCell({
          children: [
            new docx.Paragraph({
              children: [
                new docx.TextRun({
                  text: value,
                  bold: highlight,
                  size: highlight ? 22 : 20,
                  color: highlight ? COLORS.primary : COLORS.text,
                }),
              ],
              spacing: { before: 80, after: 80 },
              indent: { left: 200 },
            }),
          ],
          width: { size: 60, type: docx.WidthType.PERCENTAGE },
          verticalAlign: docx.VerticalAlign.CENTER,
        }),
      ],
    });
  };

  return new docx.Table({
    width: { size: 60, type: docx.WidthType.PERCENTAGE },
    alignment: docx.AlignmentType.CENTER,
    borders: {
      top: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      bottom: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      left: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      right: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      insideHorizontal: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
    },
    rows: [
      createRow('SOURCE DOCUMENTS', `${docCount} files`, true),
      createRow('TOTAL WORD COUNT', wordCount.toLocaleString()),
      createRow('GENERATION DATE', date),
      createRow('GENERATION TIME', time),
      createRow('ENGINE VERSION', APP_VERSION),
      createRow('LANGUAGE', 'British English'),
    ],
  });
}

function createSourcesSection(sources: SourceDocument[]): (docx.Paragraph | docx.Table)[] {
  const header = [
    new docx.Paragraph({
      children: [new docx.TextRun({ text: 'SECTION 1', bold: true, size: 18, color: COLORS.primary })],
      spacing: { before: 400, after: 80 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({ text: 'SOURCE DOCUMENTS', bold: true, size: 32, color: COLORS.text, font: 'Calibri Light' }),
      ],
      spacing: { after: 40 },
    }),
    new docx.Paragraph({
      children: [new docx.TextRun({ text: 'Original files merged into this record', size: 22, color: COLORS.textLight })],
      spacing: { after: 300 },
      border: { bottom: { color: COLORS.primary, space: 1, style: docx.BorderStyle.SINGLE, size: 8 } },
    }),
  ];

  const rows = sources.map((source, idx) => {
    const isEven = idx % 2 === 0;
    return new docx.TableRow({
      children: [
        new docx.TableCell({
          children: [
            new docx.Paragraph({
              children: [new docx.TextRun({ text: `${idx + 1}`, bold: true, size: 20, color: COLORS.white })],
              alignment: docx.AlignmentType.CENTER,
              spacing: { before: 100, after: 100 },
            }),
          ],
          width: { size: 8, type: docx.WidthType.PERCENTAGE },
          shading: { fill: COLORS.primary },
          verticalAlign: docx.VerticalAlign.CENTER,
        }),
        new docx.TableCell({
          children: [
            new docx.Paragraph({
              children: [new docx.TextRun({ text: source.filename, bold: true, size: 22, color: COLORS.text })],
              spacing: { before: 100, after: 100 },
              indent: { left: 200 },
            }),
          ],
          width: { size: 62, type: docx.WidthType.PERCENTAGE },
          shading: { fill: isEven ? COLORS.background : COLORS.white },
          verticalAlign: docx.VerticalAlign.CENTER,
        }),
        new docx.TableCell({
          children: [
            new docx.Paragraph({
              children: [
                new docx.TextRun({ text: `${source.wordCount.toLocaleString()} words`, size: 20, color: COLORS.textLight }),
              ],
              spacing: { before: 100, after: 100 },
              indent: { left: 200 },
            }),
          ],
          width: { size: 30, type: docx.WidthType.PERCENTAGE },
          shading: { fill: isEven ? COLORS.background : COLORS.white },
          verticalAlign: docx.VerticalAlign.CENTER,
        }),
      ],
    });
  });

  const table = new docx.Table({
    width: { size: 100, type: docx.WidthType.PERCENTAGE },
    borders: {
      top: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      bottom: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      left: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
      right: { style: docx.BorderStyle.SINGLE, size: 1, color: COLORS.border },
    },
    rows: [
      new docx.TableRow({
        children: [
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [new docx.TextRun({ text: '#', bold: true, size: 18, color: COLORS.white })],
                alignment: docx.AlignmentType.CENTER,
                spacing: { before: 80, after: 80 },
              }),
            ],
            shading: { fill: COLORS.primaryDark },
          }),
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [new docx.TextRun({ text: 'FILENAME', bold: true, size: 18, color: COLORS.white })],
                spacing: { before: 80, after: 80 },
                indent: { left: 200 },
              }),
            ],
            shading: { fill: COLORS.primaryDark },
          }),
          new docx.TableCell({
            children: [
              new docx.Paragraph({
                children: [new docx.TextRun({ text: 'WORD COUNT', bold: true, size: 18, color: COLORS.white })],
                spacing: { before: 80, after: 80 },
                indent: { left: 200 },
              }),
            ],
            shading: { fill: COLORS.primaryDark },
          }),
        ],
      }),
      ...rows,
    ],
  });

  return [...header, table];
}

function createSummarySection(summary: string): docx.Paragraph[] {
  const header = [
    new docx.Paragraph({
      children: [new docx.TextRun({ text: 'SECTION 2', bold: true, size: 18, color: COLORS.primary })],
      spacing: { before: 400, after: 80 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({ text: 'EXECUTIVE SUMMARY', bold: true, size: 32, color: COLORS.text, font: 'Calibri Light' }),
      ],
      spacing: { after: 40 },
    }),
    new docx.Paragraph({
      children: [new docx.TextRun({ text: 'AI-generated overview of consolidated content', size: 22, color: COLORS.textLight })],
      spacing: { after: 300 },
      border: { bottom: { color: COLORS.primary, space: 1, style: docx.BorderStyle.SINGLE, size: 8 } },
    }),
  ];

  const paragraphs = summary.split('\n').filter(l => l.trim()).map(line => {
    const isHeading = line.startsWith('##') || line.startsWith('**');
    const cleanLine = line.replace(/^#+\s*/, '').replace(/\*\*/g, '');

    return new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: isHeading ? '▸ ' : '',
          color: COLORS.primary,
          size: 22,
        }),
        new docx.TextRun({
          text: cleanLine,
          bold: isHeading,
          size: isHeading ? 24 : 22,
          color: isHeading ? COLORS.text : COLORS.textLight,
        }),
      ],
      spacing: { before: isHeading ? 200 : 80, after: 80 },
      indent: { left: isHeading ? 0 : 400 },
    });
  });

  return [...header, ...paragraphs];
}

function createContentSection(content: string, includeReferences: boolean): docx.Paragraph[] {
  const sectionNum = includeReferences ? '3' : '2';
  
  const header = [
    new docx.Paragraph({
      children: [new docx.TextRun({ text: `SECTION ${sectionNum}`, bold: true, size: 18, color: COLORS.primary })],
      spacing: { before: 400, after: 80 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({ text: 'CONSOLIDATED TRANSCRIPT', bold: true, size: 32, color: COLORS.text, font: 'Calibri Light' }),
      ],
      spacing: { after: 40 },
    }),
    new docx.Paragraph({
      children: [new docx.TextRun({ text: 'Complete merged content in British English', size: 22, color: COLORS.textLight })],
      spacing: { after: 300 },
      border: { bottom: { color: COLORS.primary, space: 1, style: docx.BorderStyle.SINGLE, size: 8 } },
    }),
  ];

  const lines = content.split('\n');
  const paragraphs = lines.map(line => {
    // Check for document separator markers
    if (line.startsWith('---') || line.startsWith('===')) {
      return new docx.Paragraph({
        children: [new docx.TextRun({ text: '─'.repeat(50), color: COLORS.border, size: 20 })],
        alignment: docx.AlignmentType.CENTER,
        spacing: { before: 300, after: 300 },
      });
    }

    // Check for source document header
    if (line.startsWith('[SOURCE:') || line.startsWith('## Document')) {
      return new docx.Paragraph({
        children: [
          new docx.TextRun({
            text: line.replace(/[\[\]#]/g, '').trim(),
            bold: true,
            size: 24,
            color: COLORS.primary,
          }),
        ],
        spacing: { before: 400, after: 200 },
        shading: { fill: COLORS.backgroundAlt },
        indent: { left: 200, right: 200 },
      });
    }

    // Check for timestamp
    const timestampMatch = line.match(/^\[(\d+:\d+(?::\d+)?)\]/);
    if (timestampMatch) {
      const timestamp = timestampMatch[1];
      const rest = line.substring(timestampMatch[0].length);
      
      return new docx.Paragraph({
        children: [
          new docx.TextRun({ text: `[${timestamp}]`, bold: true, size: 18, color: COLORS.primary, font: 'Consolas' }),
          new docx.TextRun({ text: `  ${rest}`, size: 21, color: COLORS.text }),
        ],
        spacing: { before: 120, after: 80 },
      });
    }

    // Check for speaker line
    const speakerMatch = line.match(/^([A-Z][a-zA-Z\s]+):\s*/);
    if (speakerMatch) {
      const speaker = speakerMatch[1];
      const dialogue = line.substring(speakerMatch[0].length);
      
      return new docx.Paragraph({
        children: [
          new docx.TextRun({ text: `${speaker}: `, bold: true, size: 21, color: COLORS.primaryDark }),
          new docx.TextRun({ text: dialogue, size: 21, color: COLORS.text }),
        ],
        spacing: { before: 80, after: 80 },
        indent: { left: 400 },
      });
    }

    // Regular paragraph
    return new docx.Paragraph({
      children: [new docx.TextRun({ text: line || ' ', size: 21, color: COLORS.text })],
      spacing: { before: 80, after: 80 },
      indent: { left: 400 },
    });
  });

  return [...header, ...paragraphs];
}

function createPageBreak(): docx.Paragraph {
  return new docx.Paragraph({
    children: [],
    pageBreakBefore: true,
  });
}

function createEndSection(): docx.Paragraph[] {
  return [
    new docx.Paragraph({
      children: [new docx.TextRun({ text: '◆ ◆ ◆', size: 28, color: COLORS.primary })],
      alignment: docx.AlignmentType.CENTER,
      spacing: { before: 800, after: 200 },
    }),
    new docx.Paragraph({
      children: [new docx.TextRun({ text: 'END OF CONSOLIDATED RECORD', bold: true, size: 20, color: COLORS.textMuted })],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 100 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: `Generated by LinguistPro ${APP_VERSION}`,
          size: 18,
          italics: true,
          color: COLORS.textMuted,
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
      spacing: { after: 100 },
    }),
    new docx.Paragraph({
      children: [
        new docx.TextRun({
          text: 'All content translated to British English. Original context preserved.',
          size: 16,
          color: COLORS.textMuted,
        }),
      ],
      alignment: docx.AlignmentType.CENTER,
    }),
  ];
}
